import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import employeeApi from '../lib/employeeApi';
import AccountingPage from './AccountingPage';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { HeaderSkeleton, MetricCardsSkeleton, TableSkeleton } from './skeletons/Skeletons';

/**
 * Employee Accounting Page Wrapper
 * 
 * This component fetches the employee's accounting access permissions
 * and renders the shared AccountingPage with appropriate permissions.
 * 
 * Permission Matrix (UNIFORM - Boss privileges eliminated):
 * - ALL employees: PO tab only, can create/edit own POs, view own POs only
 * - Dashboard, Invoices, Estimates, Salaries, Retainers are ADMIN-ONLY
 */
export default function EmployeeAccountingPage() {
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAccess = async () => {
      try {
        const response = await employeeApi.get('/api/employee/accounting/access');
        setAccess(response.data);
        setEmployeeId(response.data.employee_id);
      } catch (err) {
        console.error('Failed to fetch accounting access:', err);
        setError('Failed to load access permissions');
        toast.error('Failed to load accounting access');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAccess();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="employee-accounting-loading">
        <HeaderSkeleton withActions={false} />
        <MetricCardsSkeleton count={3} />
        <TableSkeleton rows={5} columns={4} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6" data-testid="employee-accounting-error">
        <div className="max-w-lg mx-auto text-center py-16">
          <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-[#1a1a2e] mb-2">Error</h2>
          <p className="text-neutral-600">{error}</p>
        </div>
      </div>
    );
  }

  // ALL employees get 'regular' role - Boss privileges have been eliminated
  // is_boss_department is now always false from the backend
  const employeeRole = 'regular';

  // All employees have uniform access - PO-only access
  // The AccountingPage component handles permissions internally based on employeeRole

  return (
    <AccountingPage 
      mode="employee" 
      employeeRole={employeeRole}
      currentEmployeeId={employeeId}
    />
  );
}
